import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ApplicationState } from 'store/store.inteface';
import {
  deleteFileAction,
  setOpenClaimDataStep,
  uploadFileAction,
} from 'store/modules/openClaim/actions';
import { fixedCards } from 'utils/openClaimRequiredDocs';
import { useParams } from 'react-router-dom';
import { FilesCallback } from 'components/UploadInput';
import {
  getPresignedPostData,
  updateUploadedFiles,
} from 'services/openClaimService';
import { Label } from '../sharedStyles';
import ButtonContainer from '../components/ButtonContainer';
import { Wrapper, CardUploadContainer, Warning, LabelWarning } from './styles';
import CardWarning from './CardWarning/CardWarning';
import CardDeclarationUpload from './CardDeclarationUpload/CardDeclarationUpload';
import ModalSaveDraft from '../components/ModalSaveDraft';
import CardUpload from './CardUpload/CardUpload';
import useModalSaveDraft from '../useModalSaveDraft';

export const Step4 = (): JSX.Element => {
  const dispatch = useDispatch();
  const { id: claimId } = useParams<Record<string, string>>();
  const initialState: any = {};

  const { cpId } = useSelector(
    (state: ApplicationState) => state.storesContext,
  );

  const stateClaim = useSelector((state: ApplicationState) => state.openClaim);
  const documents = stateClaim.step.step1.tipos?.data?.documents;
  const { files } = stateClaim.step.step4 || [];
  const [loadingFiles, setLoadingFiles] = useState<FilesCallback[]>([]);
  const [filteredFiles, setFilteredFiles] = useState<{ [key: string]: any }>(
    {},
  );

  const documentsNotFixed = useMemo(
    () =>
      documents.filter(
        (doc: any) => !fixedCards.find((card: any) => card === doc.name),
      ),
    [documents],
  );

  const {
    openModal,
    setOpenModal,
    stateModal,
    setStateModal,
    // onClickForward,
    saveDraftAndLeave,
    onClickCancel,
    // onError,
    savingDraft,
    setSavingDraft,
  } = useModalSaveDraft('step4');

  const [validationState, setValidationState] = useState(initialState);
  const [disabled, setDisabled] = useState(true);
  // esse array eh usado para separar os campos obrigatorios no layout
  // e portanto a ordem do array eh importante

  const validation = useCallback(() => {
    if (Object.values(validationState).some(x => x === false)) {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
  }, [validationState]);

  const onClickForward = () => {
    if (!savingDraft) {
      setSavingDraft(true);
      dispatch(setOpenClaimDataStep(validationState, 'step4', 'forward'));
    }
  };

  const handleSubmit = () => {
    saveDraftAndLeave(validationState);
  };

  useEffect(() => {
    // para recordar valores do rascunho
    if (stateClaim.step.step4 !== null) {
      setValidationState((prevState: any) => ({
        ...prevState,
      }));
    }
  }, [stateClaim.step.step4.state]);

  useEffect(() => {
    validation();
  }, [validationState]);

  const acceptedFiles = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'application/pdf',
    'application/excel',
    'application/vnd.ms-excel',
    'application/msexcel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.template',
    'application/vnd.ms-excel.sheet.macroenabled.12',
    'application/vnd.ms-excel.template.macroenabled.12',
    'application/vnd.ms-excel.addin.macroenabled.12',
    'application/vnd.ms-excel.sheet.binary.macroenabled.12',
    'video/avi',
    'video/wmv',
    'video/mp4',
    'video/x-ms-wmv',
  ];

  const uploadValidFiles = async () => {
    try {
      const filesSuccess: any[] = [];
      const filesError: any[] = [];

      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < loadingFiles.length; i++) {
        // eslint-disable-next-line no-await-in-loop
        const { data: presignedPostData } = await getPresignedPostData(
          loadingFiles[i].file.data.name,
          cpId,
          stateClaim.step.step1.lojas?.value,
        );
        const reader = new FileReader();
        reader.readAsArrayBuffer(loadingFiles[i].file.data);
        reader.onload = () => {
          console.log('onload', reader.result);
          fetch(presignedPostData.response.URL, {
            method: 'PUT',
            body: reader.result,
          }).then(async response => {
            if (response.status === 200) {
              console.log('File was successfully uploaded!');
              // o serviço da etapa 3 necessita do nome do documento (alinhar com back-end caso não seja possivel obter aqui)
              filesSuccess.push({
                documentId: loadingFiles[i].idDoc,
                filename: loadingFiles[i].file.data.name,
                id: presignedPostData.response.key,
              });
            } else {
              filesError.push({
                filename: loadingFiles[i].file.data.filename,
                id: loadingFiles[i].file.data.filename + Date.now(),
                error: true,
                status: 'error',
                documentId: loadingFiles[i].idDoc,
              });
            }
            // no momento está aqui, mas com a implementação de array de promisse deverá ser executada apenas após a validação de toda a pilha (Promise.all)
            if (i === loadingFiles.length - 1) {
              const uploadedFiles = filesSuccess.map(file => ({
                Key: file.id,
                fileName: file.filename,
                fieldName: file.documentId,
              }));

              await updateUploadedFiles(
                uploadedFiles,
                claimId,
                cpId,
                stateClaim.step.step1.lojas.value,
              );

              dispatch(
                uploadFileAction(loadingFiles, claimId, {
                  errors: filesError,
                  success: filesSuccess,
                }),
              );
            }
          });
        };
      }
    } catch (error: any) {
      console.log('An error occurred!', error.message);
      const errorsFiles = loadingFiles.map(file => ({
        filename: file.file.data.name,
        id: file.file.name + Date.now(),
        error: true,
        status: 'error',
        documentId: file.idDoc,
      }));
      dispatch(
        uploadFileAction(loadingFiles, claimId, {
          errors: errorsFiles,
          success: [],
        }),
      );
    }
  };

  useEffect(() => {
    let filtered = {};

    fixedCards.forEach(card => {
      filtered = {
        ...filtered,
        [card]: files?.filter(
          (file: any) =>
            file.documentId ===
            documents?.find((doc: any) => doc.name === card).id,
        ),
      };
    });
    documentsNotFixed.forEach((doc: any) => {
      filtered = {
        ...filtered,
        [doc.name]: files?.filter((file: any) => file.documentId === doc.id),
      };
    });

    setFilteredFiles(filtered);
  }, [files, documents, documentsNotFixed]);

  useEffect(() => {
    if (loadingFiles.length > 0) {
      uploadValidFiles();
      setLoadingFiles([]);
    }
  }, [loadingFiles]);

  const deleteFiles = (idDoc: string) => {
    dispatch(deleteFileAction(idDoc, claimId));
  };

  return (
    <Wrapper>
      <CardWarning />

      {documents && stateClaim.draftId && (
        <>
          <Label>Insira aqui todos os documentos necessários</Label>
          <CardDeclarationUpload
            idDoc={documents?.find((doc: any) => doc.name === fixedCards[0]).id}
            title="Declaração sobre outros seguros"
            setState={setValidationState}
            callbackFunc={newFiles =>
              setLoadingFiles(loading => [...loading, ...newFiles])
            }
            acceptedFiles={acceptedFiles}
            deleteFile={deleteFiles}
            files={filteredFiles[fixedCards[0]] || []}
            cpCode={cpId}
            franchiseCode={stateClaim.step.step1.lojas.value}
          />
          <CardUploadContainer>
            {fixedCards.slice(1).map((card: string) => (
              <CardUpload
                key={documents?.find((doc: any) => doc.name === card).id}
                idDoc={documents?.find((doc: any) => doc.name === card).id}
                title={card}
                required
                setState={setValidationState}
                callbackFunc={newFiles =>
                  setLoadingFiles(loading => [...loading, ...newFiles])
                }
                deleteFile={deleteFiles}
                acceptedFiles={acceptedFiles}
                files={filteredFiles[card] || []}
              />
            ))}
          </CardUploadContainer>
        </>
      )}
      <Warning>
        <span>*</span> Documentos obrigatórios para abertura de sinistro
      </Warning>
      <LabelWarning>
        ENVIE OS DOCUMENTOS ABAIXO PARA AGILIZAR ESTE
        <br />
        PROCESSO E EVITAR UM REENVIO DE DOCUMENTOS
      </LabelWarning>
      <CardUploadContainer>
        {documents &&
          stateClaim.draftId &&
          documentsNotFixed?.map((doc: any) => {
            return (
              <CardUpload
                key={doc.id}
                idDoc={doc.id}
                title={doc.name}
                setState={setValidationState}
                // required={doc.mandatory} //por enquanto cards obrigatorios desativados
                required={false}
                // typeFiles={doc.type}
                callbackFunc={newFiles =>
                  setLoadingFiles(loading => [...loading, ...newFiles])
                }
                acceptedFiles={acceptedFiles}
                deleteFile={deleteFiles}
                files={filteredFiles[doc.name] || []}
              />
            );
          })}
      </CardUploadContainer>
      <Warning>
        Novos documentos poderão ser solicitados durante este processo.
      </Warning>
      <ButtonContainer
        onClickForward={onClickForward}
        onClickCancel={onClickCancel}
        disabled={disabled}
      />
      <ModalSaveDraft
        stateModal={stateModal}
        setStateModal={setStateModal}
        isOpen={openModal}
        setIsOpen={setOpenModal}
        handleSubmit={handleSubmit}
      />
    </Wrapper>
  );
};
